import { AgGridButtons } from "@/controls/buttons/AgGridButtons";
import { ManualAutoButton } from "@/pages/caster/caster-data-entry-v2/components/buttons/ManualAutoButton";
import { StrandData } from "@/pages/caster/models/StrandData";
import { formatNumberWithDecimals } from "@/utils/decimals-formatter";
import { Grid } from "@material-ui/core";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";

interface StrandProperty {
	name: string;
	startField: keyof StrandData | null;
	endField: keyof StrandData | null;
	numberFormat: string | null;
}

const FormattedNumberCell = (params: ICellRendererParams) => {
	const { value, data } = params;
	const formatter = data?.numberFormat as string | null;
	if (value == null || typeof value === "string") return null;
	return <>{formatNumberWithDecimals(value, formatter || "")}</>;
};

export const useStrandTableData = (
	strands: StrandData[],
	onEditClick: (strandNum: number) => void,
	onDownloadClick: (strandNum: number) => void,
	loading: boolean,
) => {
	const strandProperties: StrandProperty[] = [
		{
			name: "Mold Water T in",
			startField: "MWTempInStart",
			endField: "MWTempInEnd",
			numberFormat: "XXX.X",
		},
		{
			name: "Mold Water DT",
			startField: "MWDTStart",
			endField: "MWDTEnd",
			numberFormat: "XXX.X",
		},
		{
			name: "Spray Pres N",
			startField: "NSprayStart",
			endField: "NSprayEnd",
			numberFormat: "XX",
		},
		{
			name: "Spray Pres E",
			startField: "ESprayStart",
			endField: "ESprayEnd",
			numberFormat: "XX",
		},
		{
			name: "Spray Pres S",
			startField: "SSprayStart",
			endField: "SSprayEnd",
			numberFormat: "XX",
		},
		{
			name: "Spray Pres W",
			startField: "WSprayStart",
			endField: "WSprayEnd",
			numberFormat: "XX",
		},
	];

	const dataByStrand: Record<string, StrandData> = strands.reduce(
		(acc, cur) => {
			if (cur.Strand != null) {
				acc[cur.Strand.toString()] = cur;
			}
			return acc;
		},
		{} as Record<string, StrandData>,
	);

	const rowData = strandProperties.map((prop) => ({
		id: prop.name,
		Property: prop.name,
		s1_start: prop.startField ? dataByStrand["1"]?.[prop.startField] : null,
		s1_end: prop.endField ? dataByStrand["1"]?.[prop.endField] : null,
		s2_start: prop.startField ? dataByStrand["2"]?.[prop.startField] : null,
		s2_end: prop.endField ? dataByStrand["2"]?.[prop.endField] : null,
		s3_start: prop.startField ? dataByStrand["3"]?.[prop.startField] : null,
		s3_end: prop.endField ? dataByStrand["3"]?.[prop.endField] : null,
		numberFormat: prop.numberFormat,
	}));

	rowData.push({
		id: "actions-row",
		Property: "",
		s1_start: "ACTIONS",
		s1_end: null,
		s2_start: "ACTIONS",
		s2_end: null,
		s3_start: "ACTIONS",
		s3_end: null,
		numberFormat: null,
	});

	const columns: (ColDef | ColGroupDef)[] = [
		{
			field: "Property",
			headerName: "",
			minWidth: 100,
			cellClass: "first-column-cell",
			suppressMovable: true,
		},
		...[1, 2, 3].map((strandNum, index) => {
			const isManual = strands.find(
				(s) => s.Strand === strandNum,
			)?.IsStrandDataManual;
			const startFieldKey = `s${strandNum}_start`;
			const endFieldKey = `s${strandNum}_end`;

			// Determine cell style based on the index of the strand group
			const cellStyle =
				index % 2 !== 0 ? { backgroundColor: "rgba(245, 245, 245, 0.67)" } : {};

			return {
				headerName: `Strand ${strandNum}`,
				headerGroupComponent: () => {
					return (
						<Grid container alignItems="center" spacing={1}>
							<Grid item xs={5}>
								<span>Strand {strandNum}</span>
							</Grid>
							<Grid item xs={7}>
								<ManualAutoButton isManual={isManual || false} />
							</Grid>
						</Grid>
					);
				},
				children: [
					{
						headerName: "Start",
						field: startFieldKey,
						minWidth: 70,
						flex: 1,
						colSpan: (params) => {
							return params.data?.id === "actions-row" ? 2 : 1;
						},

						cellRendererSelector: (params: ICellRendererParams) => {
							if (params.value === "ACTIONS") {
								return {
									component: AgGridButtons,
									params: {
										editItem: () => {
											onEditClick(strandNum);
										},
										downloadItem: () => {
											onDownloadClick(strandNum);
										},
										editLabel: `Edit strand ${strandNum}`,
										downloadLabel: "Download from PLC",
										loading,
									},
								};
							} else {
								return { component: FormattedNumberCell };
							}
						},
						cellStyle: (params) => {
							const baseStyle = {
								...cellStyle,
								textAlign: "right" as const,
							};

							if (params.data?.id === "actions-row") {
								return {
									...baseStyle,
									textAlign: "center" as const,
								};
							}

							return baseStyle;
						},
					},
					{
						headerName: "End",
						field: endFieldKey,
						minWidth: 70,
						flex: 1,
						cellRendererSelector: (params: ICellRendererParams) => {
							if (params.data?.Property === "Actions") {
								return null;
							} else {
								return { component: FormattedNumberCell };
							}
						},
						cellStyle: (params) => {
							const baseStyle = {
								...cellStyle,
								textAlign: "right" as const,
							};

							if (params.data?.id === "actions-row") {
								return {
									...baseStyle,
									textAlign: "center" as const,
								};
							}

							return baseStyle;
						},
					},
				],
			} as ColGroupDef;
		}),
	];

	const defaultColDef: ColDef = {
		filter: false,
		floatingFilter: false,
		sortable: false,
		resizable: false,
		suppressHeaderMenuButton: true,
		flex: 1,
	};

	return {
		columns,
		defaultColDef,
		rowData,
	};
};
